import * as z from "zod";
import { UseFormReturn } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Input } from "@/components/ui/input.tsx";
import { H4 } from "@/components/ui/h4.tsx";
import { Button } from "@/components/ui/button";
import { Bot } from "lucide-react";

export const genderSchema = z.enum(["male", "female"]);

export const appearanceFormSchema = z.object({
  age: z.coerce
    .number()
    .min(1, "Age must be greater than or equal to 1")
    .int("Age must be a whole number")
    .optional(),
  gender: genderSchema.optional(),
  hair_color: z.string(),
  eye_color: z.string(),
  skin_tone: z.string(),
  body_type: z.string(),
  fashion: z.string(),
  height: z.string(),
  weight: z.string(),
  ethnicity: z.string(),
});

const FORM_KEYS = appearanceFormSchema.keyof().options;

export type PersonaAppearance = z.infer<typeof appearanceFormSchema>;

const LABEL_MAP = {
  age: "Age",
  gender: "Gender",
  hair_color: "Hair Color",
  eye_color: "Eye Color",
  skin_tone: "Skin Tone",
  body_type: "Body Type",
  fashion: "Fashion",
  height: "Height",
  weight: "Weight",
  ethnicity: "Ethnicity",
};

type AppearanceStepProps = {
  readOnly: boolean;
  form: UseFormReturn<PersonaAppearance>;
  onQuickCreateClick?: () => void;
};

export function AppearanceStep({
  readOnly,
  form,
  onQuickCreateClick,
}: AppearanceStepProps) {
  return (
    <Form {...form}>
      <form className="flex flex-col gap-y-1">
        <div className="flex justify-between items-center mb-2">
          <H4 className="text-primary">Define Your AI's Visual Identity</H4>
          {onQuickCreateClick && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onQuickCreateClick}
              disabled={readOnly}
            >
              <Bot className="mr-2 h-4 w-4" />
              AI Quick Create
            </Button>
          )}
        </div>

        <p className="text-sm text-white mt-0 mb-5 align-top">
          Design your AI's physical appearance by specifying key visual traits -
          from hair color and fashion style to body type and ethnicity. These
          characteristics guide the AI's image generation, ensuring consistent
          and personalized visual representation across all interactions.
        </p>

        <div className="grid grid-cols-2 gap-x-4 gap-y-4">
          {FORM_KEYS.map((key, index) => (
            <FormField
              key={key}
              control={form.control}
              name={key}
              disabled={readOnly}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{LABEL_MAP[key]}</FormLabel>
                  <FormControl>
                    {key === "gender" ? (
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={String(field.value)}
                        disabled={field.disabled}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={key === "age" ? "number" : "text"}
                        autoFocus={index === 0}
                        {...field}
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>
      </form>
    </Form>
  );
}
